/**
 * Shared raise-on-focus counter for floating panels. Each panel that opts in
 * registers an id; the most recently raised panel gets the highest offset
 * within its ladder rung (LAYER_Z + 0..MAX_RAISE_OFFSET).
 */
import { useCallback, useEffect, useSyncExternalStore } from "react";

import { LAYER_Z, MAX_RAISE_OFFSET } from "./floatingPanelConstants";

let raiseSeq = 0;
const raisedAt = new Map();
const listeners = new Set();

const emit = () => listeners.forEach((fn) => fn());

export function raisePanel(id) {
  if (raiseSeq > 0 && raisedAt.get(id) === raiseSeq) return;
  raiseSeq += 1;
  raisedAt.set(id, raiseSeq);
  emit();
}

export function releasePanel(id) {
  if (raisedAt.delete(id)) emit();
}

/** Rank of `id` among registered panels by raise order (0 = lowest). */
export function raiseOffset(id) {
  const mine = raisedAt.get(id);
  if (mine == null) return 0;
  let below = 0;
  raisedAt.forEach((seq) => { if (seq < mine) below += 1; });
  return Math.min(below, MAX_RAISE_OFFSET);
}

export const zIndexFor = (state, offset = 0) => (LAYER_Z[state] ?? 0) + offset;

const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/** Test-only reset of the module-level registry. */
export function resetPanelLayers() {
  raiseSeq = 0;
  raisedAt.clear();
  emit();
}

/**
 * @returns {[number, () => void]} the panel's current offset and a raise fn.
 * Disabled panels always read 0 and never register.
 */
export function useRaiseOrder(id, enabled) {
  const offset = useSyncExternalStore(
    subscribe,
    () => (enabled ? raiseOffset(id) : 0),
    () => 0,
  );
  useEffect(() => {
    if (!enabled) return undefined;
    raisePanel(id);
    return () => releasePanel(id);
  }, [id, enabled]);
  const raise = useCallback(() => { if (enabled) raisePanel(id); }, [id, enabled]);
  return [offset, raise];
}
